import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import { AuthProvider, useAuthContext } from "./contexts/AuthContext";
import Home from "./pages/Home";
import Dashboard from "./pages/Dashboard";
import Transactions from "./pages/Transactions";
import Charts from "./pages/Charts";
import Goals from "./pages/Goals";
import Budgets from "./pages/Budgets";
import Reports from "./pages/Reports";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import Admin from "./pages/Admin";
import WhatsAppSettings from "./pages/WhatsAppSettings";
import BetaExpired from "./pages/BetaExpired";
import InviteOnly from "./pages/InviteOnly";
import AccountSuspended from "./pages/AccountSuspended";
import VoiceEntry from "./pages/VoiceEntry";
import VoiceConfirm from "./pages/VoiceConfirm";
import Preferences from "./pages/Preferences";
import Bills from "./pages/Bills";
import Landing from "./pages/Landing";
import TermsOfUse from "./pages/TermsOfUse";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import LegalConsent from "./pages/LegalConsent";
import { LEGAL_VERSION } from "../../shared/legal";
import { DashboardLayoutSkeleton } from "./components/DashboardLayoutSkeleton";
import { useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { I18nProvider } from "@/contexts/I18nContext";

/**
 * AuthGuard — wraps a single protected page component.
 *
 * Reads auth state from AuthProvider (single query instance at the top of the
 * tree). This avoids the insertBefore error caused by multiple components
 * calling useAuth() simultaneously and creating concurrent query instances.
 *
 * Rules:
 * 1. useAuthContext() and useLocation() are ALWAYS called — no conditional hooks.
 * 2. Redirect happens inside useEffect, NOT during render.
 * 3. While loading or unauthenticated, render a stable skeleton tree.
 *    Never return null or swap between different component trees mid-render.
 */
function AuthGuard({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading, user } = useAuthContext();
  const location = useLocation();
  const navigate = useNavigate();

  // Check beta status — only when authenticated and not admin
  const betaQuery = trpc.admin.getBetaStatus.useQuery(undefined, {
    enabled: isAuthenticated && !loading,
    staleTime: 5 * 60 * 1000, // cache for 5 min
  });

  // Check invite-only access for current user
  const inviteQuery = trpc.admin.checkMyAccess.useQuery(undefined, {
    enabled: isAuthenticated && !loading && user?.role !== "admin",
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // Consulta com autenticação simples para permitir que contas suspensas vejam
  // a explicação, mesmo quando todas as APIs de uso já estão bloqueadas.
  const accountAccessQuery = trpc.admin.getMyAccessStatus.useQuery(undefined, {
    enabled: isAuthenticated && !loading && user?.role !== "admin",
    staleTime: 0,
    retry: false,
  });

  useEffect(() => {
    if (loading) return;
    if (isAuthenticated) return;
    navigate(`/login?next=${encodeURIComponent(location.pathname)}`, { replace: true });
  }, [loading, isAuthenticated, location.pathname, navigate]);

  // Stable tree while loading or redirecting — never null, never swap
  if (loading || !isAuthenticated) {
    return <DashboardLayoutSkeleton />;
  }

  if (user?.role !== "admin" && (accountAccessQuery.data?.suspended || accountAccessQuery.data?.paymentRequired)) {
    return <AccountSuspended paymentRequired={accountAccessQuery.data?.paymentRequired} />;
  }

  // O aceite é solicitado apenas na primeira entrada ou quando a versão legal muda.
  if (!user?.legalAcceptedAt || user.legalVersion !== LEGAL_VERSION) {
    return <LegalConsent />;
  }

  // Show beta expired screen if beta has ended (admins bypass)
  if (betaQuery.data?.expired && user?.role !== "admin") {
    return <BetaExpired betaExpiry={betaQuery.data.betaExpiry} />;
  }

  // Show invite-only screen if mode is active and user is not in the list
  // Also catch the error case (FORBIDDEN/INVITE_ONLY) for users already logged in
  // when invite-only mode is activated mid-session.
  const isInviteBlocked =
    user?.role !== "admin" &&
    (inviteQuery.data?.blocked === true ||
      (inviteQuery.isError &&
        inviteQuery.error?.data?.code === "FORBIDDEN" &&
        inviteQuery.error?.message === "INVITE_ONLY"));
  if (isInviteBlocked) {
    return <InviteOnly />;
  }

  return <>{children}</>;
}

/**
 * Router — react-router-dom Routes with each route listed EXACTLY ONCE.
 */
function Router() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Home />} />
      <Route path="/termos-de-uso" element={<TermsOfUse />} />
      <Route path="/politica-de-privacidade" element={<PrivacyPolicy />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/404" element={<NotFound />} />

      {/* Admin route — protected, admin-only check is inside the page */}
      <Route path="/admin" element={<AuthGuard><Admin /></AuthGuard>} />

      {/* Protected routes — each listed ONCE, individually guarded */}
      <Route path="/dashboard" element={<AuthGuard><Dashboard /></AuthGuard>} />
      <Route path="/transactions" element={<AuthGuard><Transactions /></AuthGuard>} />
      <Route path="/charts" element={<AuthGuard><Charts /></AuthGuard>} />
      <Route path="/goals" element={<AuthGuard><Goals /></AuthGuard>} />
      <Route path="/budgets" element={<AuthGuard><Budgets /></AuthGuard>} />
      <Route path="/reports" element={<AuthGuard><Reports /></AuthGuard>} />
      <Route path="/settings/whatsapp" element={<AuthGuard><WhatsAppSettings /></AuthGuard>} />
      <Route path="/settings/preferences" element={<AuthGuard><Preferences /></AuthGuard>} />
      <Route path="/voice" element={<AuthGuard><VoiceEntry /></AuthGuard>} />
      <Route path="/voice/confirm" element={<AuthGuard><VoiceConfirm /></AuthGuard>} />
      <Route path="/bills" element={<AuthGuard><Bills /></AuthGuard>} />

      {/* Fallback */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light" switchable>
        <TooltipProvider>
          {/*
           * AuthProvider is intentionally OUTSIDE BrowserRouter.
           * This prevents React from trying to reconcile auth state changes
           * (trpc query updates) at the same time as route transitions,
           * which caused the insertBefore DOM corruption error.
           * The trpc client does not depend on router context.
           */}
          <AuthProvider>
            {/*
             * I18nProvider is intentionally INSIDE AuthProvider.
             * This ensures that when I18nProvider reads the auth.me cache to
             * sync language/currency preferences, AuthProvider has already
             * populated the React Query cache. Placing I18nProvider outside
             * AuthProvider (e.g. in main.tsx) caused a second concurrent
             * trpc.auth.me.useQuery() instance, triggering the insertBefore
             * DOM corruption error in production.
             */}
            <I18nProvider>
              <BrowserRouter>
                <Router />
              </BrowserRouter>
              {/*
               * Toaster is intentionally OUTSIDE BrowserRouter.
               * When Toaster is inside the router, React's getHostSibling() tries
               * to find a sibling node during route transitions, but the sibling
               * was already removed by the route change, causing the insertBefore
               * NotFoundError. Keeping Toaster as a stable sibling outside the
               * router tree prevents this race condition entirely.
               */}
              <Toaster />
            </I18nProvider>
          </AuthProvider>
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
